import { useGetAsset } from 'ClientPortal/src/api/Queries/assets'
import { useGetOrders } from 'ClientPortal/src/api/Queries/orders'
import { useGetValuations } from 'ClientPortal/src/api/Queries/valuations'
import { useGetValueChecks } from 'ClientPortal/src/api/Queries/value-checks'
import useTabPath from 'ClientPortal/src/router/useTabPath'
import { useTypedParams } from 'ClientPortal/src/router/useTypedParams'
import { flatten, maxBy } from 'lodash'
import dayjs from 'dayjs'
import { throwOnForbiddenApiError } from 'ClientPortal/src/api/react-query-config'

type UseAssetPageLayoutParams = {
    withoutHistorical?: boolean
}

export function useAssetPageLayout({ withoutHistorical = false }: UseAssetPageLayoutParams = {}) {
    const { assetId } = useTypedParams(['assetId'])
    const selectedTab = useTabPath(['overview', 'valuations', 'deviation-analysis'])
    const { asset, isAssetPending } = useGetAsset({ assetId, throwOnError: throwOnForbiddenApiError })
    const { valuations: allValuations, isValuationsPending } = useGetValuations({
        assetId,
        includeDrafts: true,
        filterFinalized: false,
    })
    const valuations = allValuations.filter((valuation) => valuation.status === 'finalised' || valuation.is_draft)
    const { valueChecks, isValueChecksPending } = useGetValueChecks({ assetId })
    const { orders, isOrdersPending } = useGetOrders({ assetId })

    const latestFinalizedValuationOrDraft = maxBy(
        withoutHistorical ? valuations.filter((entry) => entry.valuation_type !== 'historical') : valuations,
        (entry) => [entry.is_draft ? 0 : 1, dayjs(entry.valuation_date).valueOf(), dayjs(entry.created_on).valueOf()]
    )
    const latestValuationOrValueCheck = maxBy([...valuations, ...valueChecks], (entry) => [
        'is_draft' in entry ? (entry.is_draft ? 0 : 1) : 1,
        dayjs(entry.valuation_date).valueOf(),
        dayjs(entry.created_on).valueOf(),
    ])

    const allReferencedDraftIds = flatten(valuations.map((v) => v.drafts ?? []))
    const unlinkedDrafts = valuations.filter((valuation) => valuation.is_draft && !allReferencedDraftIds.includes(valuation.uuid))
    const latestUnlinkedDraft = maxBy(unlinkedDrafts, (v) => dayjs(v.created_on).valueOf())
    const parentValuationForLatestUnlinkedDraft = latestUnlinkedDraft
        ? allValuations.find((valuation) => !valuation.is_draft && valuation.drafts.includes(latestUnlinkedDraft.uuid))
        : undefined

    const latestOrder = maxBy(orders, 'created_on')
    const latestDraftOrder = orders.find((order) => order.uuid === latestUnlinkedDraft?.order_uuid)

    return {
        assetId,
        selectedTab,
        asset,
        latestFinalizedValuationOrDraft,
        latestValuationOrValueCheck,
        latestUnlinkedDraft,
        latestUnlinkedDraftParentValuationStatus: parentValuationForLatestUnlinkedDraft?.status,
        showPlausibility: !!latestDraftOrder && !latestDraftOrder.external_participant_mailbox,
        latestOrder,
        valuations,
        isAssetLayoutPending: isAssetPending || isValuationsPending || isValueChecksPending || isOrdersPending,
    }
}
